/**
 * TruckList Component
 * Header and list wrapper for truck cards
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { TruckCard } from './TruckCard';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { Truck, OCRFieldType, OCRLoadingState } from '../types';

interface TruckListProps {
  trucks: Truck[];
  expandedTruckId: string | null;
  ocrLoading: OCRLoadingState;
  serviceHistoryLoading: { [key: string]: boolean };
  vinInputRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
  licenseInputRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
  onAddTruck: () => void;
  onToggleExpand: (truckId: string) => void;
  onTruckUpdate: (truckId: string, field: string, value: string) => void;
  onRegistrationDateChange: (truckId: string, date: string) => void;
  onDeleteClick: (truck: Truck) => void;
  onVinCamera: (truckId: string) => void;
  onLicenseCamera: (truckId: string) => void;
  onImageCapture: (truckId: string, fieldType: OCRFieldType, event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddServiceEntry: (truckId: string) => void;
  onUpdateServiceStatus: (truckId: string, entryId: string, currentStatus: string) => void;
  onUpdateServiceDate: (truckId: string, entryId: string, newDate: string) => void;
  onDeleteServiceEntry: (truckId: string, entryId: string) => void;
  onVehicleSelected?: (truckId: string, vehicle: any) => void;
}

export const TruckList: React.FC<TruckListProps> = ({
  trucks,
  expandedTruckId,
  ocrLoading,
  serviceHistoryLoading,
  vinInputRefs,
  licenseInputRefs,
  onAddTruck,
  onToggleExpand,
  onTruckUpdate,
  onRegistrationDateChange,
  onDeleteClick,
  onVinCamera,
  onLicenseCamera,
  onImageCapture,
  onAddServiceEntry,
  onUpdateServiceStatus,
  onUpdateServiceDate,
  onDeleteServiceEntry,
  onVehicleSelected,
}) => {
  const { canDeleteTrucks, canAddServiceRecords } = usePermissions();

  return (
    <Box sx={{ px: 3, pb: 3 }}>
      {/* Header Info and Actions */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              color: '#6b7280',
            }}
          >
            Total {trucks.length} {trucks.length === 1 ? 'truck' : 'trucks'}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            color: '#ef4444',
            fontWeight: 500,
            textAlign: { xs: 'left', md: 'center' },
            px: { xs: 0, md: 2 },
          }}
        >
          Carb check due bi-annually and determined by registration date
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddTruck}
          sx={{
            bgcolor: '#3b82f6',
            minHeight: { xs: 48, sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              bgcolor: '#2563eb',
            },
          }}
        >
          Add Truck
        </Button>
      </Box>

      {/* Truck Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {trucks.map((truck) => (
          <TruckCard
            key={truck.id}
            truck={truck}
            expanded={expandedTruckId === truck.id}
            ocrLoading={ocrLoading}
            serviceHistoryLoading={serviceHistoryLoading[truck.id] || false}
            vinInputRef={(el) => { vinInputRefs.current[truck.id] = el; }}
            licenseInputRef={(el) => { licenseInputRefs.current[truck.id] = el; }}
            onToggleExpand={onToggleExpand}
            onTruckUpdate={onTruckUpdate}
            onRegistrationDateChange={onRegistrationDateChange}
            onDeleteClick={canDeleteTrucks ? onDeleteClick : () => {}}
            onVinCamera={onVinCamera}
            onLicenseCamera={onLicenseCamera}
            onImageCapture={onImageCapture}
            onAddServiceEntry={canAddServiceRecords ? onAddServiceEntry : () => {}}
            onUpdateServiceStatus={canAddServiceRecords ? onUpdateServiceStatus : () => {}}
            onUpdateServiceDate={canAddServiceRecords ? onUpdateServiceDate : () => {}}
            onDeleteServiceEntry={canAddServiceRecords ? onDeleteServiceEntry : () => {}}
            canDelete={canDeleteTrucks}
            canEditService={canAddServiceRecords}
            onVehicleSelected={onVehicleSelected}
          />
        ))}
      </Box>
    </Box>
  );
};
